import { useEffect } from 'react';
import type { NavItem } from '../../data/nav';

const SITE_ORIGIN = 'https://truenooch.com';

/**
 * Per-route <title>, meta description, canonical and robots.
 *
 * Why this exists: until 2026-08-30 the site was hash-routed, so Google saw
 * exactly one address with one title and one description for the whole brand.
 * Real paths are only half the fix — each path also has to say what it is.
 *
 * LIMITATION, on purpose: this is a client-rendered SPA, so every route serves
 * the same index.html and these tags are written by JS after render. Google
 * executes JS and will read them. A crawler that does not will see only the
 * static defaults in public/index.html. Fixing that properly means
 * prerendering to static HTML per route — deliberately out of scope here
 * ("not a rewrite"), and the reason the static defaults in index.html are
 * still written to stand on their own.
 */
function setMeta(selector: string, create: () => HTMLElement, value: string) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  if (el.tagName === 'LINK') el.setAttribute('href', value);
  else el.setAttribute('content', value);
}

export function usePageMeta(item: NavItem | undefined) {
  useEffect(() => {
    if (!item) return;

    document.title = item.title;

    setMeta(
      'meta[name="description"]',
      () => Object.assign(document.createElement('meta'), { name: 'description' }),
      item.description,
    );

    // og/twitter mirror the description so shared links match the page.
    setMeta(
      'meta[property="og:description"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:description');
        return m;
      },
      item.description,
    );
    setMeta(
      'meta[property="og:title"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:title');
        return m;
      },
      item.title,
    );
    setMeta(
      'meta[property="og:url"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:url');
        return m;
      },
      SITE_ORIGIN + item.path,
    );

    setMeta(
      'link[rel="canonical"]',
      () => Object.assign(document.createElement('link'), { rel: 'canonical' }),
      SITE_ORIGIN + item.path,
    );

    // Crawling stays allowed even when noindex — a page that can't be
    // crawled can't have its noindex read, and would still get indexed
    // from inbound links.
    setMeta(
      'meta[name="robots"]',
      () => Object.assign(document.createElement('meta'), { name: 'robots' }),
      item.noindex ? 'noindex, follow' : 'index, follow',
    );
  }, [item]);
}
